import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { notificationController } from '../controllers/notificationController';
import type { NotificationMessage } from '../data/types';
import {
  clearRealtimeNotificationState,
  deleteRealtimeNotification,
  getNotificationCenterItems,
  markRealtimeNotificationAsRead,
  notificationCenterUpdatedEvent,
  setRealtimeNotificationItems,
  upsertRealtimeNotification
} from '../data/notificationCenter';
import type { NotificationCenterItem } from '../data/notificationCenter';
import {
  createNotificationHubConnection,
  defaultRealtimeNotificationPreferences,
  getRealtimeConnectionInfo,
  getRealtimeNotificationPreferences,
  isUnauthorizedRealtimeError,
  searchRealtimeAppNotifications,
  type RealtimeNotificationPreferences,
  type RealtimeLatencyThresholds
} from '../api/signalRClient';
import { useUser } from '@/contexts/UserContext';
import { redirectToLoginWithReturnTo } from '@/feature/auth/utils/authRouting';
import {
  getCurrentTenantHost,
  getStoredTenantId
} from '@/feature/auth/utils/tenantStorage';
import {
  profileSettingsUpdatedEvent,
  type ProfileSettingsResponse
} from '@/feature/profile/api/profile.api';
import {
  applyRealtimeChatConversationRead,
  applyRealtimeChatConversationUpdated,
  applyRealtimeChatMessage,
  clearMessengerStore
} from '@/feature/messenger/data/messengerStore';

export type RealtimeConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

export type RealtimeConnectionQuality =
  | 'excellent'
  | 'good'
  | 'poor'
  | 'offline';

interface RealtimeConnectionDetails {
  connectionId?: string;
  tenantId?: string;
  userId?: string;
  serverTime?: string;
}

export interface RealtimeNotificationsState extends RealtimeConnectionDetails {
  status: RealtimeConnectionStatus;
  quality: RealtimeConnectionQuality;
  latencyMs?: number;
}

interface NotificationsContextType {
  notifications: NotificationMessage[];
  realtime: RealtimeNotificationsState;
}

const initialRealtimeState: RealtimeNotificationsState = {
  status: 'disconnected',
  quality: 'offline'
};

const fallbackLatencyThresholds: RealtimeLatencyThresholds = {
  excellentMs: 150,
  goodMs: 350,
  poorMs: 900
};

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  realtime: initialRealtimeState
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readStringValue(
  value: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'string' && item.trim()) {
      return item.trim();
    }

    if (typeof item === 'number' && Number.isFinite(item)) {
      return String(item);
    }
  }

  return undefined;
}

function readNumberValue(
  value: Record<string, unknown>,
  keys: string[]
): number | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'number' && Number.isFinite(item)) {
      return item;
    }

    if (typeof item === 'string') {
      const parsed = Number(item);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
}

function classifyConnectionQuality(
  status: RealtimeConnectionStatus,
  latencyMs: number | undefined,
  thresholds: RealtimeLatencyThresholds
): RealtimeConnectionQuality {
  if (status === 'disconnected') return 'offline';
  if (status === 'connecting' || status === 'reconnecting') return 'poor';
  if (latencyMs === undefined) return 'good';
  if (latencyMs <= thresholds.excellentMs) return 'excellent';
  if (latencyMs <= thresholds.goodMs) return 'good';

  return 'poor';
}

function toLegacyNotification(
  notification: NotificationCenterItem
): NotificationMessage {
  return {
    id: notification.id,
    title: notification.title,
    content: notification.description,
    timestamp: notification.createdAt,
    type: notification.priority === 'high' ? 'warning' : 'info'
  };
}

function getNotificationsSnapshot() {
  return getNotificationCenterItems().map(toLegacyNotification);
}

function getRealtimeDetails(value: unknown): RealtimeConnectionDetails {
  if (!isRecord(value)) {
    return {};
  }

  return {
    connectionId: readStringValue(value, ['connectionId']),
    tenantId: readStringValue(value, ['tenantId']),
    userId: readStringValue(value, ['userId']),
    serverTime: readStringValue(value, ['serverTime', 'serverNow'])
  };
}

function getPongLatency(
  value: unknown,
  pendingPings: Map<number, number>
): number | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const sequence = readNumberValue(value, [
    'clientSequence',
    'sequence',
    'seq'
  ]);

  if (sequence !== undefined) {
    const sentAt = pendingPings.get(sequence);
    pendingPings.delete(sequence);

    if (sentAt !== undefined) {
      return Date.now() - sentAt;
    }
  }

  const clientSentAt = readStringValue(value, ['clientSentAt', 'sentAt']);
  const sentAtTime = clientSentAt ? new Date(clientSentAt).getTime() : NaN;

  return Number.isNaN(sentAtTime) ? undefined : Date.now() - sentAtTime;
}

function showRealtimeNotificationToast(notification: NotificationCenterItem) {
  const config = {
    message: notification.title,
    description: notification.description,
    placement: 'topRight' as const
  };

  if (notification.priority === 'high') {
    notificationController.warning(config);
    return;
  }

  notificationController.info(config);
}

function isNotificationEnabled(
  notification: NotificationCenterItem,
  preferences: RealtimeNotificationPreferences
) {
  if (!preferences.pushNotifications) {
    return false;
  }

  if (notification.category === 'message') {
    return preferences.commentNotification;
  }

  if (notification.category === 'reminder') {
    return preferences.reminderNotification;
  }

  if (notification.category !== 'order') {
    return true;
  }

  const eventType =
    `${notification.title} ${notification.description}`.toLowerCase();
  if (/komentarz|wiadomo|odpowied|conversation/.test(eventType)) {
    return preferences.commentNotification;
  }

  if (/status|zmian/.test(eventType)) {
    return preferences.statusNotification;
  }

  return preferences.newOrderNotification;
}

export const NotificationsProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { accessToken, user, setAccessToken, setPermissions, setUser } =
    useUser();
  const [notifications, setNotifications] = useState<NotificationMessage[]>(
    () => getNotificationsSnapshot()
  );
  const [realtime, setRealtime] =
    useState<RealtimeNotificationsState>(initialRealtimeState);
  const connectionRef = useRef<HubConnection | null>(null);
  const pendingPingsRef = useRef(new Map<number, number>());
  const latencyThresholdsRef = useRef(fallbackLatencyThresholds);
  const notificationPreferencesRef = useRef<RealtimeNotificationPreferences>(
    defaultRealtimeNotificationPreferences
  );
  const pingSequenceRef = useRef(0);
  const runIdRef = useRef(0);
  const currentUserKey = user
    ? String(user.id || user.email?.name || user.userName || 'user')
    : '';
  const currentUserId = user ? String(user.id) : undefined;
  const tenantScope = accessToken
    ? `${getStoredTenantId()}:${getCurrentTenantHost()}`
    : '';

  useEffect(() => {
    const updatePreferences = (event: Event) => {
      const settings = (event as CustomEvent<ProfileSettingsResponse>).detail;
      if (!settings) return;

      notificationPreferencesRef.current = {
        pushNotifications: settings.pushNotifications,
        newOrderNotification: settings.newOrderNotification,
        commentNotification: settings.commentNotification,
        statusNotification: settings.statusNotification,
        reminderNotification: settings.reminderNotification
      };
    };

    window.addEventListener(profileSettingsUpdatedEvent, updatePreferences);
    return () =>
      window.removeEventListener(
        profileSettingsUpdatedEvent,
        updatePreferences
      );
  }, []);

  useEffect(() => {
    const refreshNotifications = () => {
      setNotifications(getNotificationsSnapshot());
    };

    window.addEventListener(
      notificationCenterUpdatedEvent,
      refreshNotifications
    );
    window.addEventListener('storage', refreshNotifications);

    return () => {
      window.removeEventListener(
        notificationCenterUpdatedEvent,
        refreshNotifications
      );
      window.removeEventListener('storage', refreshNotifications);
    };
  }, []);

  useEffect(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    let disposed = false;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let connection: HubConnection | null = null;
    const pendingPings = pendingPingsRef.current;
    const tokenScope = accessToken ?? '';

    const updateStatus = (status: RealtimeConnectionStatus) => {
      setRealtime((current) => ({
        ...current,
        status,
        quality: classifyConnectionQuality(
          status,
          current.latencyMs,
          latencyThresholdsRef.current
        )
      }));
    };

    const clearUnauthorizedSession = () => {
      window.sessionStorage.removeItem('accessToken');
      window.sessionStorage.removeItem('refreshToken');
      setAccessToken(null);
      setPermissions(null);
      setUser(null);
      clearRealtimeNotificationState();
      clearMessengerStore();
      redirectToLoginWithReturnTo();
    };

    const clearTimer = () => {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = undefined;
      }
    };

    const handleReceived = (payload: unknown) => {
      const notification = upsertRealtimeNotification(payload);

      if (
        notification &&
        isNotificationEnabled(notification, notificationPreferencesRef.current)
      ) {
        showRealtimeNotificationToast(notification);
      }
    };

    const handleRead = (payload: unknown) => {
      markRealtimeNotificationAsRead(payload);
    };

    const handleDeleted = (payload: unknown) => {
      deleteRealtimeNotification(payload);
    };

    const handleConnectionInfo = (payload: unknown) => {
      const details = getRealtimeDetails(payload);

      setRealtime((current) => ({
        ...current,
        ...details
      }));
    };

    const handlePong = (payload: unknown) => {
      const latencyMs = getPongLatency(payload, pendingPings);

      if (latencyMs === undefined) {
        return;
      }

      setRealtime((current) => ({
        ...current,
        latencyMs,
        quality: classifyConnectionQuality(
          current.status,
          latencyMs,
          latencyThresholdsRef.current
        )
      }));
    };

    const handleChatMessageReceived = (payload: unknown) => {
      applyRealtimeChatMessage(payload, currentUserId);
    };

    const handleChatConversationUpdated = (payload: unknown) => {
      applyRealtimeChatConversationUpdated(payload, currentUserId);
    };

    const handleChatConversationRead = (payload: unknown) => {
      applyRealtimeChatConversationRead(payload);
    };

    const detachHandlers = () => {
      if (!connection) return;

      connection.off('AppNotificationReceived', handleReceived);
      connection.off('AppNotificationRead', handleRead);
      connection.off('AppNotificationDeleted', handleDeleted);
      connection.off('RealtimeConnectionInfo', handleConnectionInfo);
      connection.off('RealtimePong', handlePong);
      connection.off('ChatMessageReceived', handleChatMessageReceived);
      connection.off('ChatConversationUpdated', handleChatConversationUpdated);
      connection.off('ChatConversationRead', handleChatConversationRead);
    };

    const stopConnection = () => {
      clearTimer();
      pendingPings.clear();
      detachHandlers();

      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }

      if (connection) {
        void connection.stop().catch((error) => {
          console.warn('SignalR stop failed:', error);
        });
      }
    };

    if (!accessToken || !currentUserKey) {
      stopConnection();
      clearRealtimeNotificationState();
      clearMessengerStore();
      setRealtime(initialRealtimeState);
      return stopConnection;
    }

    const start = async () => {
      updateStatus('connecting');

      try {
        const connectionInfo = await getRealtimeConnectionInfo(accessToken);

        if (disposed || runIdRef.current !== runId) {
          return;
        }

        latencyThresholdsRef.current = connectionInfo.latencyThresholds;
        setRealtime((current) => ({
          ...current,
          connectionId: connectionInfo.connectionId,
          tenantId: connectionInfo.tenantId,
          userId: connectionInfo.userId,
          serverTime: connectionInfo.serverTime,
          status: 'connecting',
          quality: classifyConnectionQuality(
            'connecting',
            current.latencyMs,
            connectionInfo.latencyThresholds
          )
        }));

        try {
          notificationPreferencesRef.current =
            await getRealtimeNotificationPreferences(accessToken);
        } catch (error) {
          if (isUnauthorizedRealtimeError(error)) {
            clearUnauthorizedSession();
            return;
          }

          console.warn('Notification preferences fetch failed:', error);
        }

        try {
          const appNotifications =
            await searchRealtimeAppNotifications(accessToken);

          if (!disposed && runIdRef.current === runId) {
            setRealtimeNotificationItems(appNotifications);
          }
        } catch (error) {
          if (isUnauthorizedRealtimeError(error)) {
            clearUnauthorizedSession();
            return;
          }

          console.warn('App notifications fetch failed:', error);
        }

        const sendPing = () => {
          if (!connection || disposed) return;

          const clientSequence = pingSequenceRef.current + 1;
          pingSequenceRef.current = clientSequence;
          const sentAt = Date.now();

          pendingPings.set(clientSequence, sentAt);
          void connection
            .invoke('Ping', {
              clientSentAt: new Date(sentAt).toISOString(),
              clientSequence
            })
            .then((payload) => {
              if (payload) {
                handlePong(payload);
              }
            })
            .catch((error) => {
              pendingPings.delete(clientSequence);
              console.warn('SignalR ping failed:', error);
            });
        };

        connection = createNotificationHubConnection({
          connectionInfo,
          accessTokenFactory: () =>
            window.sessionStorage.getItem('accessToken') || tokenScope
        });
        connectionRef.current = connection;

        connection.on('AppNotificationReceived', handleReceived);
        connection.on('AppNotificationRead', handleRead);
        connection.on('AppNotificationDeleted', handleDeleted);
        connection.on('RealtimeConnectionInfo', handleConnectionInfo);
        connection.on('RealtimePong', handlePong);
        connection.on('ChatMessageReceived', handleChatMessageReceived);
        connection.on('ChatConversationUpdated', handleChatConversationUpdated);
        connection.on('ChatConversationRead', handleChatConversationRead);

        connection.onreconnecting(() => {
          if (disposed || runIdRef.current !== runId) return;

          setRealtime((current) => ({
            ...current,
            status: 'reconnecting',
            quality: 'poor'
          }));
        });

        connection.onreconnected((connectionId) => {
          if (disposed || runIdRef.current !== runId) return;

          setRealtime((current) => ({
            ...current,
            connectionId: connectionId ?? current.connectionId,
            status: 'connected',
            quality: classifyConnectionQuality(
              'connected',
              current.latencyMs,
              connectionInfo.latencyThresholds
            )
          }));
          sendPing();
        });

        connection.onclose((error) => {
          if (disposed || runIdRef.current !== runId) return;

          if (error) {
            console.warn('SignalR connection closed:', error);
          }

          setRealtime((current) => ({
            ...current,
            status: 'disconnected',
            quality: 'offline'
          }));
        });

        await connection.start();

        if (disposed || runIdRef.current !== runId) {
          await connection.stop();
          return;
        }

        setRealtime((current) => ({
          ...current,
          connectionId: connection?.connectionId ?? current.connectionId,
          status: 'connected',
          quality: classifyConnectionQuality(
            'connected',
            current.latencyMs,
            connectionInfo.latencyThresholds
          )
        }));
        sendPing();
        pingTimer = setInterval(
          sendPing,
          Math.max(15000, Math.min(connectionInfo.pingIntervalMs, 30000))
        );
      } catch (error) {
        if (disposed || runIdRef.current !== runId) {
          return;
        }

        if (isUnauthorizedRealtimeError(error)) {
          clearUnauthorizedSession();
          return;
        }

        console.warn('SignalR connection failed:', error);
        setRealtime((current) => ({
          ...current,
          status: 'disconnected',
          quality: 'offline'
        }));
      }
    };

    void start();

    return () => {
      disposed = true;
      stopConnection();
    };
  }, [
    accessToken,
    currentUserId,
    currentUserKey,
    setAccessToken,
    setPermissions,
    setUser,
    tenantScope
  ]);

  const value = useMemo(
    () => ({
      notifications,
      realtime
    }),
    [notifications, realtime]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotificationsContext = () => useContext(NotificationsContext);
